var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { memo } from 'react';
import { IconButton, ListItem, ListItemText, ListItemSecondaryAction, } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/CancelOutlined';
import { useTranslate, useListFilterContext } from 'ra-core';
import { shallowEqual } from 'react-redux';
import matches from 'lodash/matches';
import pickBy from 'lodash/pickBy';
var useStyles = makeStyles(function (theme) { return ({
    listItem: {
        paddingLeft: '2em',
    },
    listItemText: {
        margin: 0,
    },
}); });
/**
 * Button to enable/disable a list filter.
 *
 * Expects 2 props:
 *
 * - label: The text to be displayed for this item. Will be translated.
 * - value: An object to be merged into the filter value when enabling the filter
 * (e.g. { is_published: true, published_at_gte: '2020-07-08' })
 *
 * @example
 *
 * import * as React from 'react';
 * import { Card, CardContent } from '@material-ui/core';
 * import MailIcon from '@material-ui/icons/MailOutline';
 * import { FilterList, FilterListItem } from 'react-admin';
 *
 * const FilterSidebar = () => (
 *     <Card>
 *         <CardContent>
 *             <FilterList
 *                 label="Subscribed to newsletter"
 *                 icon={<MailIcon />}
 *             >
 *                 <FilterListItem
 *                     label="Yes"
 *                     value={{ has_newsletter: true }}
 *                  />
 *                 <FilterListItem
 *                     label="No"
 *                     value={{ has_newsletter: false }}
 *                  />
 *             </FilterList>
 *         </CardContent>
 *     </Card>
 * );
 *
 * @example // The value prop can contain multiple keys
 *
 * import * as React from 'react';
 * import {
 *     endOfYesterday,
 *     startOfWeek,
 *     subWeeks,
 *     startOfMonth,
 *     subMonths,
 * } from 'date-fns';
 * import { Card, CardContent } from '@material-ui/core';
 * import AccessTimeIcon from '@material-ui/icons/AccessTime';
 * import { FilterList, FilterListItem } from 'react-admin';
 *
 * const FilterSidebar = () => (
 *     <Card>
 *         <CardContent>
 *             <FilterList
 *                 label="Last visited"
 *                 icon={<AccessTimeIcon />}
 *             >
 *                 <FilterListItem
 *                     label="Today"
 *                     value={{
 *                         last_seen_gte: endOfYesterday().toISOString(),
 *                         last_seen_lte: undefined,
 *                     }}
 *                 />
 *                 <FilterListItem
 *                     label="This week"
 *                     value={{
 *                         last_seen_gte: startOfWeek(
 *                             new Date()
 *                         ).toISOString(),
 *                         last_seen_lte: undefined,
 *                     }}
 *                 />
 *                 <FilterListItem
 *                     label="Last week"
 *                     value={{
 *                         last_seen_gte: subWeeks(
 *                             startOfWeek(new Date()),
 *                             1
 *                         ).toISOString(),
 *                         last_seen_lte: startOfWeek(
 *                             new Date()
 *                         ).toISOString(),
 *                     }}
 *                 />
 *                 <FilterListItem
 *                     label="This month"
 *                     value={{
 *                         last_seen_gte: startOfMonth(
 *                             new Date()
 *                         ).toISOString(),
 *                         last_seen_lte: undefined,
 *                     }}
 *                 />
 *                 <FilterListItem
 *                     label="Last month"
 *                     value={{
 *                         last_seen_gte: subMonths(
 *                             startOfMonth(new Date()),
 *                             1
 *                         ).toISOString(),
 *                         last_seen_lte: startOfMonth(
 *                             new Date()
 *                         ).toISOString(),
 *                     }}
 *                 />
 *                 <FilterListItem
 *                     label="Earlier"
 *                     value={{
 *                         last_seen_gte: undefined,
 *                         last_seen_lte: subMonths(
 *                             startOfMonth(new Date()),
 *                             1
 *                         ).toISOString(),
 *                     }}
 *                 />
 *             </FilterList>
 *         </CardContent>
 *     </Card>
 * );
 */
var FilterListItem = function (props) {
    var label = props.label, value = props.value;
    var _a = useListFilterContext(), filterValues = _a.filterValues, setFilters = _a.setFilters;
    var translate = useTranslate();
    var classes = useStyles(props);
    var isSelected = matches(pickBy(value, function (val) { return typeof val !== 'undefined'; }))(filterValues);
    var addFilter = function () {
        setFilters(__assign(__assign({}, filterValues), value), null, false);
    };
    var removeFilter = function () {
        var keysToRemove = Object.keys(value);
        var filters = Object.keys(filterValues).reduce(function (acc, key) {
            var _a;
            return keysToRemove.includes(key)
                ? acc
                : __assign(__assign({}, acc), (_a = {}, _a[key] = filterValues[key], _a));
        }, {});
        setFilters(filters, null, false);
    };
    var toggleFilter = function () { return (isSelected ? removeFilter() : addFilter()); };
    return (React.createElement(ListItem, { button: true, onClick: toggleFilter, selected: isSelected, className: classes.listItem },
        React.createElement(ListItemText, { primary: translate(label, { _: label }), className: classes.listItemText, "data-selected": isSelected ? 'true' : 'false' }),
        isSelected && (React.createElement(ListItemSecondaryAction, null,
            React.createElement(IconButton, { size: "small", onClick: toggleFilter },
                React.createElement(CancelIcon, null))))));
};
var arePropsEqual = function (prevProps, nextProps) {
    return prevProps.label === nextProps.label &&
        shallowEqual(prevProps.value, nextProps.value);
};
export default memo(FilterListItem, arePropsEqual);
